import { useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { localDateStr } from "@/pages/entregas/entregasHelpers";
import { isAdminLike, isSuperAdmin, hasPerm } from "@/lib/roles";
import { RotasResumo } from "@/pages/dashboard/RotasResumo";
import { DashboardConsolidado } from "@/pages/dashboard/DashboardConsolidado";
import { DashboardStatCards } from "@/pages/dashboard/DashboardStatCards";
import { DashboardFiltersCard } from "@/pages/dashboard/DashboardFiltersCard";
import { DashboardCharts } from "@/pages/dashboard/DashboardCharts";
import { EmpresaWelcomeCard, DashboardExportMenu, DashboardTabs, Totals7dWidget } from "@/pages/dashboard/DashboardHeaderParts";
import { useDashboardData } from "@/pages/dashboard/useDashboardData";
import { useDashboardExport } from "@/pages/dashboard/useDashboardExport";
import { useSuperAdminActions, SuperAdminDialogs } from "@/pages/dashboard/useSuperAdminActions";
import { defaultFilters, periodRange, activePeriodKey, filtrosAtivos as calcFiltrosAtivos } from "@/pages/dashboard/dashboardFilters";

export default function Dashboard() {
  const { user, empresas, currentEmpresaId, refreshEmpresas } = useAuth();
  const isAdmin = isAdminLike(user);
  const isSuperadmin = isSuperAdmin(user);
  // Matrix-first: recurso "financeiro" controla valores monetários (SA sempre vê).
  const canSeeFinanceiro = isSuperadmin || hasPerm(user, "financeiro", "v");
  // Iter 96: filtro sempre abre no dia atual (De = Até = hoje).
  const todayStr = localDateStr();
  const [filters, setFilters] = useState(() => defaultFilters(todayStr));
  const [tab, setTab] = useState("empresa");
  const dashboardRef = useRef(null); // conteúdo exportado como PDF visual (só a aba "empresa")

  const empresaAtiva = useMemo(() => empresas?.find((e) => e.id === currentEmpresaId) || null, [empresas, currentEmpresaId]);
  const data = useDashboardData({ filters, currentEmpresaId, isAdmin, tab, empresas });
  const exp = useDashboardExport({ filters, dashboardRef, empresaNome: empresaAtiva?.nome });
  const sa = useSuperAdminActions({ refreshEmpresas, reloadCons: data.reloadCons });

  const applyPeriod = (kind) => { const r = periodRange(kind, todayStr); if (r) setFilters((f) => ({ ...f, ...r })); };
  const activePeriod = activePeriodKey(filters, todayStr);
  const filtrosAtivos = calcFiltrosAtivos(filters, todayStr);
  const showEmpresa = tab === "empresa" || !isAdmin;

  if (!data.s) return <div className="p-8 text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="page-dashboard">
      <EmpresaWelcomeCard empresa={empresaAtiva} />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral · aplique filtros para segmentar</p>
        </div>
        {/* Toolbar marcada com data-exclude-from-pdf pra não aparecer no snapshot exportado */}
        <div className="flex items-center gap-2 flex-wrap" data-exclude-from-pdf="true">
          {showEmpresa && (
            <DashboardExportMenu exportingRep={exp.exportingRep} exportingPdf={exp.exportingPdf} filtrosAtivos={filtrosAtivos}
              onExportReport={exp.exportReport} onExportPainel={exp.exportPainelPdf} />
          )}
          {isSuperadmin && <DashboardTabs tab={tab} setTab={setTab} />}
        </div>
      </div>

      {showEmpresa && (
        <div ref={dashboardRef} className="space-y-6" data-testid="dashboard-export-container">
          <Totals7dWidget totals7d={data.totals7d} />
          <DashboardFiltersCard
            filters={filters} setFilters={setFilters} activePeriod={activePeriod} onApplyPeriod={applyPeriod}
            onClear={() => setFilters(defaultFilters(todayStr))} filtrosAtivos={filtrosAtivos}
            entregadores={data.entregadores} regioes={data.regioes} tipos={data.tipos} bairrosFiltrados={data.bairrosFiltrados} />
          <DashboardStatCards s={data.s} canSeeFinanceiro={canSeeFinanceiro} onApplyPeriod={applyPeriod} />
          <RotasResumo rotas={data.s.by_rota} />
          <DashboardCharts s={data.s} canSeeFinanceiro={canSeeFinanceiro} />
        </div>
      )}

      {isSuperadmin && tab === "consolidado" && (
        <DashboardConsolidado
          empresas={empresas}
          cons={data.cons}
          filters={filters}
          setFilters={setFilters}
          onNovaEmpresa={() => sa.setNovaEmpresaOpen(true)}
          onModoApresentacao={sa.openModoApresentacao}
          onBaixarPdfApresentacao={exp.baixarRelatorioApresentacao}
          onLaunchEmpresa={sa.setLaunchEmpresa}
          onPurgeEmpresa={sa.setPurgeEmpresa}
        />
      )}

      <SuperAdminDialogs sa={sa} empresas={empresas} refreshEmpresas={refreshEmpresas} reloadCons={data.reloadCons}
        onBaixarPdfApresentacao={exp.baixarRelatorioApresentacao} />
    </div>
  );
}
